// mixins/guidance-statistics-mixin.js
export default {
    computed: {
        guidanceStatistics: function() {
            return this.calculateGuidanceStatistics();
        },

        workPointStatistics: function() {
            return this.calculateWorkPointStatistics();
        },

        shiftStatistics: function() {
            return this.calculateShiftStatistics();
        },

        dailyBreakdownData: function() {
            return this.calculateDailyBreakdown();
        }
    },

    methods: {
        // Get all dates in the guidance range with their day types
        getDateRangeWithDayTypes: function() {
            var dates = [];
            if (!this.guidance || !this.guidance.start_date || !this.guidance.end_date) return dates;

            var startDate = new Date(this.guidance.start_date);
            var endDate = new Date(this.guidance.end_date);
            var currentDate = new Date(startDate);

            while (currentDate <= endDate) {
                var dayOfWeek = currentDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
                var dayType = this.getDayType(currentDate);

                dates.push({
                    date: new Date(currentDate),
                    dateString: this.formatDate(currentDate),
                    dayOfWeek: dayOfWeek,
                    dayType: dayType,
                    isWeekend: (dayOfWeek === 0 || dayOfWeek === 6),
                    isHoliday: dayType === 'Holiday'
                });

                currentDate.setDate(currentDate.getDate() + 1);
            }

            return dates;
        },

        // Get day type for a specific date
        getDayType: function(date) {
            const dayOfWeek = date.getDay();
            // 0 = Sunday, 6 = Saturday
            if (dayOfWeek === 6) return 'Saturday';
            if (dayOfWeek === 0) return 'Sunday';
            return 'Weekday';
        },

        // Check if a date has an exception for a specific activity
        hasExceptionForDate: function(activity, date) {
            if (!activity || !activity.exceptions || activity.exceptions.length === 0) {
                return false;
            }

            var dayType = this.getDayType(date);

            // Check if there's an exception matching this day type
            return activity.exceptions.some(function(exception) {
                return exception.day_type == dayType;
            });
        },

        // Get exception for a specific date
        getExceptionForDate: function(activity, date) {
            if (!activity || !activity.exceptions) return null;

            var dayType = this.getDayType(date);

            var exAc =  activity.exceptions.find(function(exception) {
                return exception.day_type == dayType;
            });
             return exAc;

        },

        // Calculate statistics for a single date
        calculateDateStats: function(date, workPoints) {
            var dateStats = {
                date: date,
                dayType: this.getDayType(date),
                workPoints: {},
                total: {
                    labourer_count: 0,
                    total_hours: 0,
                    total_amount: 0
                }
            };

            var self = this;
            workPoints.forEach(function(workPoint, wpIndex) {
                var wpDateStats = {
                    labourer_count: 0,
                    total_hours: 0,
                    total_amount: 0,
                    activities: {}
                };

                if (workPoint.activities) {
                    workPoint.activities.forEach(function(activity, actIndex) {
                        var hasException = self.hasExceptionForDate(activity, date);
                        var exceptionData = hasException ? self.getExceptionForDate(activity, date) : null;

                        // Use exception data if available, otherwise use regular activity
                        var activityData = exceptionData || activity;
                        var isException = !!exceptionData;
                        var dayType = exceptionData ? exceptionData.day_type : self.getDayType(date);

                        var activityStats = self.calculateActivityStatsForDate(
                            activityData,
                            isException,
                            dayType
                        );

                        wpDateStats.labourer_count += activityStats.labourer_count;
                        wpDateStats.total_hours += activityStats.total_hours;
                        wpDateStats.total_amount += activityStats.total_amount;
                        wpDateStats.activities[actIndex] = activityStats;
                    });
                }

                dateStats.workPoints[wpIndex] = wpDateStats;
                dateStats.total.labourer_count += wpDateStats.labourer_count;
                dateStats.total.total_hours += wpDateStats.total_hours;
                dateStats.total.total_amount += wpDateStats.total_amount;
            });

            return dateStats;
        },

        // Calculate statistics for a single date (used for daily breakdown)
        calculateActivityStatsForDate: function(activity, isException, dayType) {
            var stats = {
                labourer_count: 0,
                total_hours: 0,
                total_amount: 0,
                shifts: {}
            };

            if (!activity || (!activity.activity_shifts && !activity.activityShifts)) return stats;

            if(activity.activityShifts) activity.activity_shifts = activity.activityShifts
            var self = this;
            activity.activity_shifts.forEach(function(shiftData) {
                if (!shiftData.shift) return;

                var shiftHours = self.calculateShiftHours(shiftData.shift);
                var labourerCount = parseInt(shiftData.labourer_count) || 0;
                var amountPerLabourer = self.getActivityAmount(activity, dayType, isException);

                var totalLabourers = labourerCount;
                var totalHours = totalLabourers * shiftHours;
                var totalAmount = totalHours * amountPerLabourer;

                stats.labourer_count += totalLabourers;
                stats.total_hours += totalHours;
                stats.total_amount += totalAmount;

                stats.shifts[shiftData.shift.id] = {
                    labourer_count: totalLabourers,
                    total_hours: totalHours,
                    total_amount: totalAmount,
                    shift_hours: shiftHours,
                    rate_per_hour: amountPerLabourer
                };
            });

            return stats;
        },

        // Calculate daily breakdown for the entire guidance period
        calculateDailyBreakdown: function() {
            var self = this;
            var dates = this.getDateRangeWithDayTypes();
            var dailyStats = [];

            dates.forEach(function(dateInfo) {
                var dateStats = self.calculateDateStats(dateInfo.date, self.workPoints);
                dailyStats.push({
                    date: dateInfo.dateString,
                    dayType: dateInfo.dayType,
                    isWeekend: dateInfo.isWeekend,
                    statistics: dateStats.total
                });
            });

            return dailyStats;
        },

        // Calculate total statistics for the entire guidance period
        calculateGuidanceStatistics: function() {
            var dailyStats = this.calculateDailyBreakdown();

            var totals = {
                labourer_count: 0,
                total_hours: 0,
                total_amount: 0,
                regular_amount: 0,
                exception_amount: 0,
                weekday_amount: 0,
                weekend_amount: 0,
                holiday_amount: 0,
                total_days: dailyStats.length,
                weekday_count: 0,
                weekend_count: 0,
                holiday_count: 0,
                by_day_type: {
                    Weekday: { labourer_count: 0, total_hours: 0, total_amount: 0, days: 0 },
                    Saturday: { labourer_count: 0, total_hours: 0, total_amount: 0, days: 0 },
                    Sunday: { labourer_count: 0, total_hours: 0, total_amount: 0, days: 0 },
                    Holiday: { labourer_count: 0, total_hours: 0, total_amount: 0, days: 0 }
                }
            };

            var self = this;
            dailyStats.forEach(function(dayStat) {
                var dayType = dayStat.dayType;

                totals.labourer_count += dayStat.statistics.labourer_count;
                totals.total_hours += dayStat.statistics.total_hours;
                totals.total_amount += dayStat.statistics.total_amount;

                // Track by day type
                if (totals.by_day_type[dayType]) {
                    totals.by_day_type[dayType].labourer_count += dayStat.statistics.labourer_count;
                    totals.by_day_type[dayType].total_hours += dayStat.statistics.total_hours;
                    totals.by_day_type[dayType].total_amount += dayStat.statistics.total_amount;
                    totals.by_day_type[dayType].days += 1;
                }

                // Track regular vs exception amounts
                if (dayType === 'Holiday') {
                    totals.holiday_amount += dayStat.statistics.total_amount;
                    totals.holiday_count++;
                } else if (dayType === 'Saturday' || dayType === 'Sunday') {
                    totals.weekend_amount += dayStat.statistics.total_amount;
                    totals.weekend_count++;
                } else {
                    totals.weekday_amount += dayStat.statistics.total_amount;
                    totals.weekday_count++;
                }
            });

            totals.regular_amount = totals.weekday_amount;
            totals.exception_amount = totals.weekend_amount + totals.holiday_amount;

            return totals;
        },

        // Calculate shift statistics for the entire period
        calculateShiftStatistics: function() {
            var shiftStats = {};
            var dailyStats = this.calculateDailyBreakdown();
            var self = this;

            // Aggregate shift statistics across all dates
            dailyStats.forEach(function(dayStat) {
                var dayType = dayStat.dayType;
                var isException = (dayType === 'Saturday' || dayType === 'Sunday' || dayType === 'Holiday');
                var currentDate = new Date(dayStat.date);

                self.workPoints.forEach(function(workPoint) {
                    if (!workPoint.activities) return;

                    workPoint.activities.forEach(function(activity) {
                        var hasException = self.hasExceptionForDate(activity, currentDate);
                        var activityData = hasException ? self.getExceptionForDate(activity, currentDate) : activity;
                        var effectiveDayType = hasException && activityData ? activityData.day_type : dayType;

                        if (activityData && (activityData.activity_shifts || activityData.activityShifts)) {
                            if(activityData.activityShifts) activityData.activity_shifts = activityData.activityShifts
                            activityData.activity_shifts.forEach(function(shiftData) {
                                if (!shiftData.shift) return;

                                var shiftId = shiftData.shift.id;
                                if (!shiftId) return;

                                if (!shiftStats[shiftId]) {
                                    shiftStats[shiftId] = {
                                        shift_name: shiftData.shift.name,
                                        shift_hours: self.calculateShiftHours(shiftData.shift),
                                        labourer_count: 0,
                                        total_hours: 0,
                                        total_amount: 0,
                                        by_day_type: {
                                            Weekday: { labourer_count: 0, total_hours: 0, total_amount: 0 },
                                            Saturday: { labourer_count: 0, total_hours: 0, total_amount: 0 },
                                            Sunday: { labourer_count: 0, total_hours: 0, total_amount: 0 },
                                            Holiday: { labourer_count: 0, total_hours: 0, total_amount: 0 }
                                        }
                                    };
                                }

                                var amountPerLabourer = self.getActivityAmount(activityData, effectiveDayType, hasException);
                                var shiftHours = self.calculateShiftHours(shiftData.shift);
                                var labourerCount = parseInt(shiftData.labourer_count) || 0;
                                var totalHours = labourerCount * shiftHours;
                                var totalAmount = totalHours * amountPerLabourer;

                                shiftStats[shiftId].labourer_count += labourerCount;
                                shiftStats[shiftId].total_hours += totalHours;
                                shiftStats[shiftId].total_amount += totalAmount;

                                if (shiftStats[shiftId].by_day_type[effectiveDayType]) {
                                    shiftStats[shiftId].by_day_type[effectiveDayType].labourer_count += labourerCount;
                                    shiftStats[shiftId].by_day_type[effectiveDayType].total_hours += totalHours;
                                    shiftStats[shiftId].by_day_type[effectiveDayType].total_amount += totalAmount;
                                }
                            });
                        }
                    });
                });
            });

            return shiftStats;
        },

        // Calculate work point statistics for the entire period
        calculateWorkPointStatistics: function() {
            var wpStats = {};
            var dailyStats = this.calculateDailyBreakdown();
            var self = this;

            if (!this.workPoints) return wpStats;

            // Initialize work point stats
            this.workPoints.forEach(function(workPoint, wpIndex) {
                wpStats[wpIndex] = {
                    work_point_name: workPoint.workplace ? workPoint.workplace.name : 'Unknown',
                    total_labourers: 0,
                    total_hours: 0,
                    total_amount: 0,
                    regular_amount: 0,
                    exception_amount: 0,
                    daily_breakdown: []
                };
            });

            // Aggregate across all dates
            dailyStats.forEach(function(dayStat) {
                var currentDate = new Date(dayStat.date);
                var dayType = dayStat.dayType;
                var isException = (dayType === 'Saturday' || dayType === 'Sunday' || dayType === 'Holiday');

                self.workPoints.forEach(function(workPoint, wpIndex) {
                    var wpDayTotal = { labourer_count: 0, total_hours: 0, total_amount: 0 };

                    if (workPoint.activities) {
                        workPoint.activities.forEach(function(activity) {
                            var hasException = self.hasExceptionForDate(activity, currentDate);
                            var activityData = hasException ? self.getExceptionForDate(activity, currentDate) : activity;
                            var effectiveDayType = hasException && activityData ? activityData.day_type : dayType;

                            if (activityData && (activityData.activity_shifts || activityData.activityShifts)) {
                                if(activityData.activityShifts) activityData.activity_shifts = activityData.activityShifts
                                activityData.activity_shifts.forEach(function(shiftData) {
                                    if (!shiftData.shift) return;

                                    var amountPerLabourer = self.getActivityAmount(activityData, effectiveDayType, hasException);
                                    var shiftHours = self.calculateShiftHours(shiftData.shift);
                                    var labourerCount = parseInt(shiftData.labourer_count) || 0;
                                    var totalHours = labourerCount * shiftHours;
                                    var totalAmount = totalHours * amountPerLabourer;

                                    wpDayTotal.labourer_count += labourerCount;
                                    wpDayTotal.total_hours += totalHours;
                                    wpDayTotal.total_amount += totalAmount;
                                });
                            }
                        });
                    }

                    if (wpStats[wpIndex]) {
                        wpStats[wpIndex].total_labourers += wpDayTotal.labourer_count;
                        wpStats[wpIndex].total_hours += wpDayTotal.total_hours;
                        wpStats[wpIndex].total_amount += wpDayTotal.total_amount;

                        if (isException) {
                            wpStats[wpIndex].exception_amount += wpDayTotal.total_amount;
                        } else {
                            wpStats[wpIndex].regular_amount += wpDayTotal.total_amount;
                        }

                        wpStats[wpIndex].daily_breakdown.push({
                            date: dayStat.date,
                            dayType: dayType,
                            statistics: wpDayTotal
                        });
                    }
                });
            });

            return wpStats;
        },

        // Calculate shift hours
        calculateShiftHours: function(shift) {
            if (!shift || !shift.shift_length) return 0;
            return parseFloat(shift.shift_length) || 0;
        },

        // Get activity amount (regular or weekend/holiday rate)
        getActivityAmount: function(activity, dayType, isException) {
            if (!activity || !activity.activityTariff) return 0;

            // For exceptions (Saturday, Sunday, Holiday), use weekend_amount
            if (isException && (dayType === 'Saturday' || dayType === 'Sunday' || dayType === 'Holiday')) {
                return parseFloat(activity.activityTariff.weekend_amount) || 0;
            }

            // Regular days use normal amount
            return parseFloat(activity.activityTariff.amount) || 0;
        },

        // Get total days in guidance date range
        getTotalDays: function() {
            if (!this.guidance || !this.guidance.start_date || !this.guidance.end_date) return 0;

            var start = new Date(this.guidance.start_date);
            var end = new Date(this.guidance.end_date);
            var diffTime = Math.abs(end - start);
            var diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
            return diffDays;
        },

        // Format date
        formatDate: function(date) {
            var d = new Date(date);
            var year = d.getFullYear();
            var month = String(d.getMonth() + 1).padStart(2, '0');
            var day = String(d.getDate()).padStart(2, '0');
            return year + '-' + month + '-' + day;
        },

        // Generate summary report with date range
        generateSummaryReport: function() {
            var stats = this.guidanceStatistics;
            var dailyBreakdown = this.dailyBreakdownData;
            var shiftStats = this.shiftStatistics;
            var wpStats = this.workPointStatistics;

            // Format shift stats for report
            var formattedShiftStats = {};
            for (var shiftId in shiftStats) {
                var shift = shiftStats[shiftId];
                formattedShiftStats[shiftId] = {
                    shift_name: shift.shift_name,
                    shift_hours: shift.shift_hours,
                    total_labourers: shift.labourer_count,
                    total_hours: shift.total_hours,
                    total_amount: shift.total_amount,
                    by_day_type: shift.by_day_type
                };
            }

            // Format work point stats for report
            var formattedWpStats = {};
            for (var wpIndex in wpStats) {
                var wp = wpStats[wpIndex];
                formattedWpStats[wpIndex] = {
                    work_point_name: wp.work_point_name,
                    total_labourers: wp.total_labourers,
                    total_hours: wp.total_hours,
                    total_amount: wp.total_amount,
                    regular_amount: wp.regular_amount,
                    exception_amount: wp.exception_amount
                };
            }

            return {
                guidance: {
                    name: this.guidance.name || 'N/A',
                    number: this.guidance.guidance_number || 'N/A',
                    start_date: this.guidance.start_date || 'N/A',
                    end_date: this.guidance.end_date || 'N/A',
                    duration_days: stats.total_days || 0
                },
                totals: {
                    total_labourers: stats.labourer_count || 0,
                    total_hours: stats.total_hours || 0,
                    total_amount: stats.total_amount || 0,
                    regular_amount: stats.regular_amount || 0,
                    exception_amount: stats.exception_amount || 0,
                    weekday_amount: stats.weekday_amount || 0,
                    weekend_amount: stats.weekend_amount || 0,
                    holiday_amount: stats.holiday_amount || 0
                },
                day_type_summary: {
                    Weekday: {
                        days: stats.by_day_type ? stats.by_day_type.Weekday.days : 0,
                        labourers: stats.by_day_type ? stats.by_day_type.Weekday.labourer_count : 0,
                        hours: stats.by_day_type ? stats.by_day_type.Weekday.total_hours : 0,
                        amount: stats.by_day_type ? stats.by_day_type.Weekday.total_amount : 0
                    },
                    Saturday: {
                        days: stats.by_day_type ? stats.by_day_type.Saturday.days : 0,
                        labourers: stats.by_day_type ? stats.by_day_type.Saturday.labourer_count : 0,
                        hours: stats.by_day_type ? stats.by_day_type.Saturday.total_hours : 0,
                        amount: stats.by_day_type ? stats.by_day_type.Saturday.total_amount : 0
                    },
                    Sunday: {
                        days: stats.by_day_type ? stats.by_day_type.Sunday.days : 0,
                        labourers: stats.by_day_type ? stats.by_day_type.Sunday.labourer_count : 0,
                        hours: stats.by_day_type ? stats.by_day_type.Sunday.total_hours : 0,
                        amount: stats.by_day_type ? stats.by_day_type.Sunday.total_amount : 0
                    },
                    Holiday: {
                        days: stats.holiday_count || 0,
                        labourers: stats.by_day_type ? stats.by_day_type.Holiday.labourer_count : 0,
                        hours: stats.by_day_type ? stats.by_day_type.Holiday.total_hours : 0,
                        amount: stats.by_day_type ? stats.by_day_type.Holiday.total_amount : 0
                    }
                },
                daily_breakdown: dailyBreakdown.map(function(day) {
                    return {
                        date: day.date,
                        dayType: day.dayType,
                        labourer_count: day.statistics.labourer_count,
                        total_hours: day.statistics.total_hours,
                        total_amount: day.statistics.total_amount
                    };
                }),
                by_shift: formattedShiftStats,
                by_work_point: formattedWpStats
            };
        },

        // Format currency
        formatCurrency: function(amount) {
            if (amount === undefined || amount === null) return 'TZS 0';
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'TZS',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(amount);
        },

        // Format hours
        formatHours: function(hours) {
            if (hours === undefined || hours === null) return '0 hrs';
            return hours.toFixed(1) + ' hrs';
        },

        // Format number with commas
        formatNumber: function(number) {
            if (number === undefined || number === null) return '0';
            return new Intl.NumberFormat('en-US').format(number);
        }
    }
};